import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import hrManagementService from '../../services/hrManagementService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import NotificationToast from '../../components/ui/NotificationToast';
import HRDashboard from './components/HRDashboard';

const HRManagement = () => {
  const { userProfile } = useAuth();
  const [activeView, setActiveView] = useState('dashboard'); // 'dashboard' or 'personnel'
  const [hrUsers, setHrUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedHR, setSelectedHR] = useState(null);
  const [filters, setFilters] = useState({ search: '', department: 'all' });
  const [searchInput, setSearchInput] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    department: 'Human Resources',
    password: ''
  });

  useEffect(() => {
    loadHRUsers();
  }, [filters.department]);

  const loadHRUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await hrManagementService.getHRUsers(filters);
      if (result.success) {
        setHrUsers(result.data || []);
      } else {
        setError(result.error || 'Failed to load HR users');
      }
    } catch (err) {
      setError(err.message || 'Failed to load HR users');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAdd = () => {
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      department: 'Human Resources',
      password: ''
    });
    setSelectedHR(null);
    setShowAddModal(true);
  };

  const handleEdit = (hrUser) => {
    setFormData({
      email: hrUser.email,
      full_name: hrUser.full_name,
      phone: hrUser.phone || '',
      department: hrUser.department || 'Human Resources',
      password: ''
    });
    setSelectedHR(hrUser);
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this HR person?')) {
      return;
    }

    try {
      const result = await hrManagementService.deleteHRUser(id);
      if (result.success) {
        showNotification('HR person deleted successfully');
        await loadHRUsers();
      } else {
        showNotification(result.error || 'Failed to delete HR person', 'error');
      }
    } catch (err) {
      showNotification(err.message || 'Failed to delete HR person', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const isEdit = !!selectedHR;
      const result = isEdit
        ? await hrManagementService.updateHRUser(selectedHR.id, formData)
        : await hrManagementService.createHRUser(formData);

      if (result.success) {
        showNotification(`HR person ${isEdit ? 'updated' : 'created'} successfully`);
        setShowAddModal(false);
        setShowEditModal(false);
        setSelectedHR(null);
        setFormData({
          email: '',
          full_name: '',
          phone: '',
          department: 'Human Resources',
          password: ''
        });
        await loadHRUsers();
      } else {
        showNotification(result.error || `Failed to ${isEdit ? 'update' : 'create'} HR person`, 'error');
      }
    } catch (err) {
      showNotification(err.message || `Failed to ${selectedHR ? 'update' : 'create'} HR person`, 'error');
    }
  };

  const filteredHRUsers = hrUsers.filter(hr => {
    const matchesSearch = !filters.search || 
      hr.full_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      hr.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      hr.phone?.includes(filters.search);
    
    const matchesDepartment = filters.department === 'all' || hr.department === filters.department;
    
    return matchesSearch && matchesDepartment;
  });

  const uniqueDepartments = [...new Set(hrUsers.map(hr => hr.department).filter(Boolean))];

  // Render Personnel Management view
  const renderPersonnelManagement = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground text-sm">Loading HR users...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground">HR Personnel Management</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Manage HR personnel and their access
            </p>
          </div>
          <Button onClick={handleAdd} iconName="Plus" iconPosition="left">
            Add HR Person
          </Button>
        </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <div>
              <p className="text-red-800 font-medium">Error loading data</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Search</label>
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setFilters(prev => ({ ...prev, search: searchInput }));
                  }
                }}
              />
              <Button
                variant="outline"
                onClick={() => setFilters(prev => ({ ...prev, search: searchInput }))}
                title="Search"
              >
                <Icon name="Search" size={16} />
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Department</label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="all">All Departments</option>
              {uniqueDepartments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Department</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredHRUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                    <Icon name="Users" size={48} className="mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium text-foreground mb-2">No HR users found</h3>
                    <p className="text-sm">Create your first HR person to get started</p>
                  </td>
                </tr>
              ) : (
                filteredHRUsers.map((hr) => (
                  <tr key={hr.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-foreground">{hr.full_name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-foreground">{hr.email}</td>
                    <td className="px-6 py-4 text-sm text-foreground">{hr.phone || '-'}</td>
                    <td className="px-6 py-4 text-sm text-foreground">{hr.department || '-'}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {hr.created_at ? new Date(hr.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(hr)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(hr.id)}
                          title="Delete"
                          className="text-destructive hover:text-destructive"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedHR(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {selectedHR ? 'Edit HR Person' : 'Add HR Person'}
              </h3>
              <Button variant="ghost" size="icon" onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedHR(null); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Email *</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Department</label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Password {selectedHR ? '(leave blank to keep current)' : '*'}
                </label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!selectedHR}
                  placeholder={selectedHR ? 'Enter new password to change' : 'Default: HR@123'}
                />
              </div>
              
              <div className="flex gap-3 justify-end pt-4">
                <Button variant="outline" type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); setSelectedHR(null); }}>
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedHR ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

        {/* Notification */}
        {notification && (
          <NotificationToast
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-4">
          <Button
            variant={activeView === 'dashboard' ? 'default' : 'ghost'}
            onClick={() => setActiveView('dashboard')}
            className="flex items-center gap-2"
          >
            <Icon name="LayoutDashboard" size={16} />
            HR Dashboard
          </Button>
          <Button
            variant={activeView === 'personnel' ? 'default' : 'ghost'}
            onClick={() => setActiveView('personnel')}
            className="flex items-center gap-2"
          >
            <Icon name="Users" size={16} />
            Personnel Management
          </Button>
        </div>
      </div>

      {/* Content */}
      {activeView === 'dashboard' ? <HRDashboard /> : renderPersonnelManagement()}
    </div>
  );
};

export default HRManagement;

